export interface LanguageOption {
    code: string;
    label: string;
    nativeName: string;
    flag: string;
    category: 'indian' | 'international';
    direction?: 'ltr' | 'rtl';
}

export const ALL_SUPPORTED_LANGUAGES: LanguageOption[] = [
    // 🇮🇳 Indian Languages
    { code: 'en', label: 'English', nativeName: 'English', flag: '🇬🇧', category: 'indian', direction: 'ltr' },
    { code: 'hi', label: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳', category: 'indian', direction: 'ltr' },
    { code: 'mr', label: 'Marathi', nativeName: 'मराठी', flag: '🇮🇳', category: 'indian', direction: 'ltr' },
    { code: 'gu', label: 'Gujarati', nativeName: 'ગુજરાતી', flag: '🇮🇳', category: 'indian', direction: 'ltr' },
    { code: 'pa', label: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ', flag: '🇮🇳', category: 'indian', direction: 'ltr' },
    { code: 'bn', label: 'Bengali', nativeName: 'বাংলা', flag: '🇮🇳', category: 'indian', direction: 'ltr' },
    { code: 'ta', label: 'Tamil', nativeName: 'தமிழ்', flag: '🇮🇳', category: 'indian', direction: 'ltr' },
    { code: 'te', label: 'Telugu', nativeName: 'తెలుగు', flag: '🇮🇳', category: 'indian', direction: 'ltr' },
    { code: 'hinglish', label: 'Hinglish', nativeName: 'Hinglish', flag: '🇮🇳', category: 'indian', direction: 'ltr' },

    // 🌍 International Languages
    { code: 'es', label: 'Spanish', nativeName: 'Español', flag: '🇪🇸', category: 'international', direction: 'ltr' },
    { code: 'fr', label: 'French', nativeName: 'Français', flag: '🇫🇷', category: 'international', direction: 'ltr' },
    { code: 'de', label: 'German', nativeName: 'Deutsch', flag: '🇩🇪', category: 'international', direction: 'ltr' },
    { code: 'it', label: 'Italian', nativeName: 'Italiano', flag: '🇮🇹', category: 'international', direction: 'ltr' },
    { code: 'ar', label: 'Arabic', nativeName: 'العربية', flag: '🇦🇪', category: 'international', direction: 'rtl' },
    { code: 'pt', label: 'Portuguese', nativeName: 'Português', flag: '🇵🇹', category: 'international', direction: 'ltr' },
    { code: 'ru', label: 'Russian', nativeName: 'Русский', flag: '🇷🇺', category: 'international', direction: 'ltr' },
    { code: 'ja', label: 'Japanese', nativeName: '日本語', flag: '🇯🇵', category: 'international', direction: 'ltr' },
    { code: 'zh', label: 'Chinese (Simplified)', nativeName: '简体中文', flag: '🇨🇳', category: 'international', direction: 'ltr' },
];

// Currently active languages for UI selection (English & Hindi only)
export const SUPPORTED_LANGUAGES: LanguageOption[] = [
    { code: 'en', label: 'English', nativeName: 'English', flag: '🇬🇧', category: 'international', direction: 'ltr' },
    { code: 'hi', label: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳', category: 'indian', direction: 'ltr' },
];

export interface RsvpTranslations {
    hostedBy: string;
    days: string;
    hours: string;
    mins: string;
    eventLive: string;
    saveTheDate: string;
    when: string;
    where: string;
    addToCalendar: string;
    getDirections: string;
    willYouAttend: string;
    acceptJoy: string;
    declineRegret: string;
    maybeTentative: string;
    totalAttendees: string;
    adults: string;
    kids: string;
    dietaryHeading: string;
    dietaryDesc: string;
    dietNone: string;
    dietVeg: string;
    dietNonVeg: string;
    dietJain: string;
    dietVegan: string;
    dietHalal: string;
    dietEggetarian: string;
    accommodationHeading: string;
    accommodationDesc: string;
    accommodationYes: string;
    accommodationNo: string;
    arrivalDate: string;
    travelDetails: string;
    wishesHeading: string;
    wishesPlaceholder: string;
    submitRsvp: string;
    updateRsvp: string;
    submitting: string;
    thankYouAccepted: string;
    thankYouDeclined: string;
    thankYouMaybe: string;
    thankYouSubtitle: string;
    downloadInviteCard: string;
    editRsvpBtn: string;
    switchLanguage: string;
}

export const RSVP_TRANSLATIONS: Record<string, RsvpTranslations> = {
    en: {
        hostedBy: 'Hosted by',
        days: 'Days',
        hours: 'Hours',
        mins: 'Mins',
        eventLive: "It's Celebration Day! 🎉",
        saveTheDate: 'Save The Date',
        when: 'When',
        where: 'Where',
        addToCalendar: 'Add to Calendar',
        getDirections: 'Get Directions',
        willYouAttend: 'Will you be joining us?',
        acceptJoy: 'Joyfully Accept 🎉',
        declineRegret: 'Regretfully Decline 💌',
        maybeTentative: 'Tentative / Maybe 🤔',
        totalAttendees: 'Total Guests Attending',
        adults: 'Adults (12+ yrs)',
        kids: 'Children (under 12)',
        dietaryHeading: '🍲 Dietary Preferences',
        dietaryDesc: 'Help us arrange delicious food tailored for you and your family.',
        dietNone: 'Standard / No Preference',
        dietVeg: 'Pure Vegetarian 🥦',
        dietNonVeg: 'Non-Vegetarian 🍗',
        dietJain: 'Jain Food 🌿',
        dietVegan: 'Vegan 🌱',
        dietHalal: 'Halal 🥩',
        dietEggetarian: 'Eggetarian 🍳',
        accommodationHeading: '🏨 Accommodation & Travel Assistance',
        accommodationDesc: 'Please let us know if you require hotel stay or local transport arrangements.',
        accommodationYes: 'Yes, please arrange accommodation / stay 🏨',
        accommodationNo: 'No, will manage own stay / local resident 🚗',
        arrivalDate: 'Expected Arrival Date & Time',
        travelDetails: 'Flight / Train / Vehicle Details',
        wishesHeading: '💌 Blessings & Warm Wishes',
        wishesPlaceholder: 'Write a warm congratulatory message or special request for the host family...',
        submitRsvp: 'Submit RSVP Confirmation 💌',
        updateRsvp: 'Update My RSVP Response ✏️',
        submitting: 'Confirming your RSVP...',
        thankYouAccepted: '🎉 Wonderful! We look forward to celebrating with you!',
        thankYouDeclined: '💌 We will miss your presence, but thank you for letting us know.',
        thankYouMaybe: '⏳ Thank you! Please confirm your final plans whenever ready.',
        thankYouSubtitle: 'Your response has been successfully sent to the host family.',
        downloadInviteCard: '📥 Download E-Invitation Card',
        editRsvpBtn: '✏️ Change / Edit Response',
        switchLanguage: 'Language',
    },
    hi: {
        hostedBy: 'निमंत्रक:',
        days: 'दिन',
        hours: 'घंटे',
        mins: 'मिनट',
        eventLive: 'आज उत्सव का शुभ दिन है! 🎉',
        saveTheDate: 'शुभ मांगलिक उत्सव',
        when: 'दिनांक व समय',
        where: 'स्थान व पता',
        addToCalendar: 'कैलेंडर में जोड़ें',
        getDirections: 'रास्ता देखें (GPS)',
        willYouAttend: 'क्या आप उत्सव में पधार रहे हैं?',
        acceptJoy: 'सहर्ष आगमन स्वीकार 🎉',
        declineRegret: 'असमर्थता के लिए क्षमा 💌',
        maybeTentative: 'संभावित / विचारधीन 🤔',
        totalAttendees: 'आने वाले सदस्यों की कुल संख्या',
        adults: 'वयस्क सदस्य (12+ वर्ष)',
        kids: 'बच्चे (12 वर्ष से कम)',
        dietaryHeading: '🍲 भोजन प्राथमिकताएं',
        dietaryDesc: 'आपके और आपके परिवार के लिए स्वादिष्ट भोजन व्यवस्था हेतु कृपया चयन करें।',
        dietNone: 'सामान्य भोजन',
        dietVeg: 'शुद्ध शाकाहारी 🥦',
        dietNonVeg: 'मांसाहारी 🍗',
        dietJain: 'जैन भोजन 🌿',
        dietVegan: 'वीगन 🌱',
        dietHalal: 'हलाल 🥩',
        dietEggetarian: 'अंडाहारी 🍳',
        accommodationHeading: '🏨 आवास एवं यात्रा सहायता',
        accommodationDesc: 'कृपया सूचित करें यदि आपको होटल या स्थानीय परिवहन व्यवस्था की आवश्यकता है।',
        accommodationYes: 'हाँ, कृपया आवास व्यवस्था करें 🏨',
        accommodationNo: 'नहीं, स्वयं व्यवस्था करेंगे / स्थानीय निवासी 🚗',
        arrivalDate: 'आगमन का अनुमानित दिनांक व समय',
        travelDetails: 'ट्रेन / फ्लाइट / वाहन विवरण',
        wishesHeading: '💌 शुभ मंगलकामनाएं एवं संदेश',
        wishesPlaceholder: 'वर-वधू एवं परिवार के लिए अपने मंगल आशीष व संदेश यहाँ लिखें...',
        submitRsvp: 'RSVP पुष्टि सबमिट करें 💌',
        updateRsvp: 'RSVP अपडेट करें ✏️',
        submitting: 'आपकी प्रतिक्रिया दर्ज की जा रही है...',
        thankYouAccepted: '🎉 बहुत-बहुत धन्यवाद! हम आपके आगमन की प्रतीक्षा करेंगे!',
        thankYouDeclined: '💌 आपकी कमी खलेगी, सूचना देने के लिए सधन्यवाद।',
        thankYouMaybe: '⏳ धन्यवाद! योजना पक्की होने पर कृपया सूचित करें।',
        thankYouSubtitle: 'आपकी प्रतिक्रिया सफलतापूर्वक निमंत्रक परिवार तक पहुँचा दी गई है।',
        downloadInviteCard: '📥 निमंत्रण पत्र डाउनलोड करें',
        editRsvpBtn: '✏️ प्रतिक्रिया बदलें / संपादित करें',
        switchLanguage: 'भाषा',
    },
    mr: {
        hostedBy: 'निमंत्रक:',
        days: 'दिवस',
        hours: 'तास',
        mins: 'मिनिटे',
        eventLive: 'आज उत्सवाचा शुभ दिवस आहे! 🎉',
        saveTheDate: 'शुभ विवाह सोहळा',
        when: 'तारीख आणि वेळ',
        where: 'स्थळ आणि पत्ता',
        addToCalendar: 'कॅलेंडरमध्ये जोडा',
        getDirections: 'दिशा / रस्ता पहा (GPS)',
        willYouAttend: 'आपण सोहळ्यास उपस्थित राहणार का?',
        acceptJoy: 'आनंदाने उपस्थिती स्वीकार 🎉',
        declineRegret: 'अनुपस्थितीबद्दल क्षमस्व 💌',
        maybeTentative: 'अजून निश्चित नाही 🤔',
        totalAttendees: 'उपस्थित राहणाऱ्या सदस्यांची संख्या',
        adults: 'प्रौढ सदस्य (12+ वर्षे)',
        kids: 'लहान मुले (12 वर्षांखालील)',
        dietaryHeading: '🍲 जेवणाची पसंती',
        dietaryDesc: 'आपल्या व आपल्या कुटुंबीयांच्या जेवणाच्या योग्य व्यवस्थेसाठी कृपया नोंद करा.',
        dietNone: 'सर्वसाधारण भोजन',
        dietVeg: 'शुद्ध शाकाहारी 🥦',
        dietNonVeg: 'मांसाहारी 🍗',
        dietJain: 'जैन भोजन 🌿',
        dietVegan: 'व्हिगन 🌱',
        dietHalal: 'हलाल 🥩',
        dietEggetarian: 'अंडाहारी 🍳',
        accommodationHeading: '🏨 मुक्काम आणि प्रवास व्यवस्था',
        accommodationDesc: 'आपणास मुक्कामाची किंवा प्रवासाची व्यवस्था हवी असल्यास कृपया सांगा.',
        accommodationYes: 'होय, कृपया मुक्कामाची सोय करावी 🏨',
        accommodationNo: 'नाही, स्वतःची सोय आहे / स्थानिक रहिवासी 🚗',
        arrivalDate: 'येण्याची अंदाजे तारीख व वेळ',
        travelDetails: 'ट्रेन / फ्लाईट / वाहन तपशील',
        wishesHeading: '💌 शुभेच्या आणि मंगल आशीर्वाद',
        wishesPlaceholder: 'वर-वधूस आणि कुटुंबीयांना आपल्या प्रेमळ शुभेच्छा येथे लिहा...',
        submitRsvp: 'RSVP नोंदणी पूर्ण करा 💌',
        updateRsvp: 'RSVP बदल जतन करा ✏️',
        submitting: 'नोंदणी होत आहे...',
        thankYouAccepted: '🎉 मनःपूर्वक धन्यवाद! आपल्या स्वागतासाठी आम्ही उत्सुक आहोत!',
        thankYouDeclined: '💌 आपली उणीव भासेल, कळवल्याबद्दल धन्यवाद.',
        thankYouMaybe: '⏳ धन्यवाद! योजना निश्चित झाल्यावर कृपया कळवा.',
        thankYouSubtitle: 'आपला प्रतिसाद निमंत्रक कुटुंबाकडे यशस्वीरीत्या पाठवला आहे.',
        downloadInviteCard: '📥 निमंत्रण पत्रिका डाऊनलोड करा',
        editRsvpBtn: '✏️ प्रतिसाद संपादित करा',
        switchLanguage: 'भाषा',
    },
    gu: {
        hostedBy: 'નિમંત્રક:',
        days: 'દિવસ',
        hours: 'કલાક',
        mins: 'મિનિટ',
        eventLive: 'આજે મંગલ ઉત્સવનો દિવસ છે! 🎉',
        saveTheDate: 'શુભ લગ્ન મહોત્સવ',
        when: 'તારીખ અને સમય',
        where: 'સ્થળ અને સરનામું',
        addToCalendar: 'કેલેન્ડરમાં ઉમેરો',
        getDirections: 'નકશો / રસ્તો જુઓ (GPS)',
        willYouAttend: 'શું આપ શુભ પ્રસંગે પધારશો?',
        acceptJoy: 'સહર્ષ સ્વીકાર 🎉',
        declineRegret: 'ન આવી શકવા બદલ ક્ષમા 💌',
        maybeTentative: 'હજી નક્કી નથી 🤔',
        totalAttendees: 'આવનાર મહેમાનોની કુલ સંખ્યા',
        adults: 'મોટા સભ્યો (12+ વર્ષ)',
        kids: 'બાળકો (12 વર્ષથી નીચે)',
        dietaryHeading: '🍲 ભોજન પસંદગી',
        dietaryDesc: 'આપના અને આપના પરિવારના ભોજનની યોગ્ય વ્યવસ્થા માટે કૃપા કરીને પસંદ કરો.',
        dietNone: 'સામાન્ય ભોજન',
        dietVeg: 'શુદ્ધ શાકાહારી 🥦',
        dietNonVeg: 'માંસાહારી 🍗',
        dietJain: 'શુદ્ધ જૈન ભોજન 🌿',
        dietVegan: 'વિગન 🌱',
        dietHalal: 'હલાલ 🥩',
        dietEggetarian: 'ઈંડા સાથે 🍳',
        accommodationHeading: '🏨 રોકાણ અને યાત્રા સહાય',
        accommodationDesc: 'કૃપા કરીને જણાવો જો આપને હોટેલ કે વાહનની વ્યવસ્થાની જરૂર હોય.',
        accommodationYes: 'હા, રોકાણની વ્યવસ્થા કરશો 🏨',
        accommodationNo: 'ના, જાતે વ્યવસ્થા કરીશું 🚗',
        arrivalDate: 'પહોંચવાની અંદાજિત તારીખ અને સમય',
        travelDetails: 'ટ્રેન / ફ્લાઇટ / વાહનની વિગત',
        wishesHeading: '💌 શુભકામનાઓ અને આશીર્વાદ',
        wishesPlaceholder: 'નવદંપતી અને પરિવાર માટે આપના મંગલ આશીર્વાદ અહીં લખો...',
        submitRsvp: 'RSVP કન્ફર્મ કરો 💌',
        updateRsvp: 'RSVP અપડેટ કરો ✏️',
        submitting: 'માહિતી મોકલાઈ રહી છે...',
        thankYouAccepted: '🎉 ખૂબ ખૂબ આભાર! અમે આપના આગમનની રાહ જોઈશું!',
        thankYouDeclined: '💌 આપની ખોટ સાલશે, જણાવવા બદલ આભાર.',
        thankYouMaybe: '⏳ આભાર! નક્કી થાય ત્યારે કૃપા કરી જણાવશો.',
        thankYouSubtitle: 'આપનો પ્રતિસાદ સફળતાપૂર્વક પરિવારને મોકલી દેવાયો છે.',
        downloadInviteCard: '📥 કંકોત્રી / આમંત્રણ કાર્ડ ડાઉનલોડ કરો',
        editRsvpBtn: '✏️ પ્રતિસાદ બદલો',
        switchLanguage: 'ભાષા',
    },
    pa: {
        hostedBy: 'ਸੱਦਾ ਕਰਤਾ:',
        days: 'ਦਿਨ',
        hours: 'ਘੰਟੇ',
        mins: 'ਮਿੰਟ',
        eventLive: 'ਅੱਜ ਖੁਸ਼ੀਆਂ ਦਾ ਦਿਹਾੜਾ ਹੈ! 🎉',
        saveTheDate: 'ਸ਼ੁਭ ਅਨੰਦ ਕਾਰਜ',
        when: 'ਮਿਤੀ ਅਤੇ ਸਮਾਂ',
        where: 'ਸਥਾਨ ਅਤੇ ਪਤਾ',
        addToCalendar: 'ਕੈਲੰਡਰ ਵਿੱਚ ਸ਼ਾਮਲ ਕਰੋ',
        getDirections: 'ਰਸਤਾ ਦੇਖੋ (GPS)',
        willYouAttend: 'ਕੀ ਤੁਸੀਂ ਸਮਾਗਮ ਵਿੱਚ ਸ਼ਾਮਲ ਹੋਵੋਗੇ?',
        acceptJoy: 'ਖੁਸ਼ੀ ਨਾਲ ਸਵੀਕਾਰ 🎉',
        declineRegret: 'ਮਾਫ ਕਰਨਾ, ਨਹੀਂ ਆ ਸਕਾਂਗੇ 💌',
        maybeTentative: 'ਅਜੇ ਪੱਕਾ ਨਹੀਂ 🤔',
        totalAttendees: 'ਕੁੱਲ ਮਹਿਮਾਨਾਂ ਦੀ ਗਿਣਤੀ',
        adults: 'ਵੱਡੇ ਮੈਂਬਰ (12+ ਸਾਲ)',
        kids: 'ਬੱਚੇ (12 ਸਾਲ ਤੋਂ ਘੱਟ)',
        dietaryHeading: '🍲 ਖਾਣ-ਪੀਣ ਦੀ ਪਸੰਦ',
        dietaryDesc: 'ਆਪ ਜੀ ਅਤੇ ਪਰਿਵਾਰ ਲਈ ਖਾਣੇ ਦੇ ਉਚਿਤ ਪ੍ਰਬੰਧ ਲਈ ਚੋਣ ਕਰੋ।',
        dietNone: 'ਆਮ ਖਾਣਾ',
        dietVeg: 'ਸ਼ੁੱਧ ਸ਼ਾਕਾਹਾਰੀ 🥦',
        dietNonVeg: 'ਮਾਸਾਹਾਰੀ 🍗',
        dietJain: 'ਜੈਨ ਭੋਜਨ 🌿',
        dietVegan: 'ਵੀਗਨ 🌱',
        dietHalal: 'ਹਲਾਲ 🥩',
        dietEggetarian: 'ਆਂਡਾ ਖਾਣ ਵਾਲੇ 🍳',
        accommodationHeading: '🏨 ਰਿਹਾਇਸ਼ ਅਤੇ ਸਫ਼ਰ ਸਹਾਇਤਾ',
        accommodationDesc: 'ਜੇਕਰ ਤੁਹਾਨੂੰ ਹੋਟਲ ਜਾਂ ਗੱਡੀ ਦੇ ਪ੍ਰਬੰਧ ਦੀ ਲੋੜ ਹੈ ਤਾਂ ਕਿਰਪਾ ਕਰਕੇ ਦੱਸੋ।',
        accommodationYes: 'ਹਾਂ, ਕਿਰਪਾ ਕਰਕੇ ਰਿਹਾਇਸ਼ ਦਾ ਪ੍ਰਬੰਧ ਕਰੋ 🏨',
        accommodationNo: 'ਨਹੀਂ, ਆਪਣੇ ਆਪ ਪ੍ਰਬੰਧ ਕਰਾਂਗੇ 🚗',
        arrivalDate: 'ਪਹੁੰਚਣ ਦੀ ਅੰਦਾਜ਼ਨ ਮਿਤੀ ਅਤੇ ਸਮਾਂ',
        travelDetails: 'ਟ੍ਰੇਨ / ਫਲਾਈਟ / ਗੱਡੀ ਦਾ ਵੇਰਵਾ',
        wishesHeading: '💌 ਮੁਬਾਰਕਾਂ ਅਤੇ ਅਸੀਸਾਂ',
        wishesPlaceholder: 'ਜੋੜੀ ਅਤੇ ਪਰਿਵਾਰ ਲਈ ਆਪਣੀਆਂ ਸ਼ੁਭਕਾਮਨਾਵਾਂ ਇੱਥੇ ਲਿਖੋ...',
        submitRsvp: 'RSVP ਕਨਫਰਮ ਕਰੋ 💌',
        updateRsvp: 'RSVP ਅੱਪਡੇਟ ਕਰੋ ✏️',
        submitting: 'ਜਾਣਕਾਰੀ ਭੇਜੀ ਜਾ ਰਹੀ ਹੈ...',
        thankYouAccepted: '🎉 ਬਹੁਤ ਬਹੁਤ ਧੰਨਵਾਦ! ਅਸੀਂ ਆਪ ਜੀ ਦੀ ਉਡੀਕ ਕਰਾਂਗੇ!',
        thankYouDeclined: '💌 ਤੁਹਾਡੀ ਘਾਟ ਮਹਿਸੂਸ ਹੋਵੇਗੀ, ਦੱਸਣ ਲਈ ਧੰਨਵਾਦ।',
        thankYouMaybe: '⏳ ਧੰਨਵਾਦ! ਜਦੋਂ ਪ੍ਰੋਗਰਾਮ ਪੱਕਾ ਹੋਵੇ ਤਾਂ ਕਿਰਪਾ ਕਰਕੇ ਦੱਸਣਾ।',
        thankYouSubtitle: 'ਤੁਹਾਡਾ ਜਵਾਬ ਪਰਿਵਾਰ ਨੂੰ ਸਫਲਤਾਪੂਰਵਕ ਭੇਜ ਦਿੱਤਾ ਗਿਆ ਹੈ।',
        downloadInviteCard: '📥 ਸੱਦਾ ਪੱਤਰ ਡਾਊਨਲੋਡ ਕਰੋ',
        editRsvpBtn: '✏️ ਜਵਾਬ ਬਦਲੋ',
        switchLanguage: 'ਭਾਸ਼ਾ',
    },
    bn: {
        hostedBy: 'নিমন্ত্রণকর্তা:',
        days: 'দিন',
        hours: 'ঘণ্টা',
        mins: 'মিনিট',
        eventLive: 'আজ আনন্দের দিন! 🎉',
        saveTheDate: 'শুভ বিবাহ উৎসব',
        when: 'তারিখ ও সময়',
        where: 'স্থান ও ঠিকানা',
        addToCalendar: 'ক্যালেন্ডারে যোগ করুন',
        getDirections: 'পথ দেখুন (GPS)',
        willYouAttend: 'আপনি কি অনুষ্ঠানে উপস্থিত থাকবেন?',
        acceptJoy: 'সানন্দে উপস্থিতি স্বীকার 🎉',
        declineRegret: 'উপস্থিত হতে পারছি না 💌',
        maybeTentative: 'এখনও অনিশ্চিত 🤔',
        totalAttendees: 'উপস্থিত মোট অতিথির সংখ্যা',
        adults: 'প্রাপ্তবয়স্ক (১২+ বছর)',
        kids: 'শিশু (১২ বছরের নিচে)',
        dietaryHeading: '🍲 ভোজনের পছন্দ',
        dietaryDesc: 'আপনার ও আপনার পরিবারের জন্য উপযুক্ত খাবারের ব্যবস্থা করতে সহায়তা করুন।',
        dietNone: 'সাধারণ খাবার',
        dietVeg: 'নিরামিষ 🥦',
        dietNonVeg: 'আমিষ 🍗',
        dietJain: 'জৈন খাবার 🌿',
        dietVegan: 'ভেগান 🌱',
        dietHalal: 'হালাল 🥩',
        dietEggetarian: 'ডিমভোজী 🍳',
        accommodationHeading: '🏨 থাকার ও যাতায়াতের ব্যবস্থা',
        accommodationDesc: 'থাকার ব্যবস্থা বা গাড়ি প্রয়োজন হলে জানান।',
        accommodationYes: 'হ্যাঁ, থাকার ব্যবস্থা প্রয়োজন 🏨',
        accommodationNo: 'না, নিজস্ব ব্যবস্থা রয়েছে 🚗',
        arrivalDate: 'পৌঁছানোর সম্ভাব্য তারিখ ও সময়',
        travelDetails: 'ট্রেন / ফ্লাইট / গাড়ির বিবরণ',
        wishesHeading: '💌 শুভকামনা ও আশীর্বাদ',
        wishesPlaceholder: 'নবদম্পতি ও পরিবারের উদ্দেশ্যে আপনার বার্তা লিখুন...',
        submitRsvp: 'RSVP নিশ্চিত করুন 💌',
        updateRsvp: 'RSVP আপডেট করুন ✏️',
        submitting: 'জমা নেওয়া হচ্ছে...',
        thankYouAccepted: '🎉 অসংখ্য ধন্যবাদ! আপনার আগমনের অপেক্ষায় রইলাম!',
        thankYouDeclined: '💌 আপনার উপস্থিতি মিস করব, জানানোর জন্য ধন্যবাদ।',
        thankYouMaybe: '⏳ ধন্যবাদ! পরিকল্পনা চূড়ান্ত হলে জানাবেন।',
        thankYouSubtitle: 'আপনার প্রতিক্রিয়া সফলভাবে নিমন্ত্রণকারীর কাছে পৌঁছেছে।',
        downloadInviteCard: '📥 নিমন্ত্রণপত্র ডাউনলোড করুন',
        editRsvpBtn: '✏️ উত্তর পরিবর্তন করুন',
        switchLanguage: 'ভাষা',
    },
    ta: {
        hostedBy: 'அழைப்பாளர்:',
        days: 'நாட்கள்',
        hours: 'மணி',
        mins: 'நிமிடம்',
        eventLive: 'இன்று மங்களகரமான திருநாள்! 🎉',
        saveTheDate: 'சுப திருமண அழைப்பிதழ்',
        when: 'தேதி மற்றும் நேரம்',
        where: 'இடம் மற்றும் முகவரி',
        addToCalendar: 'காலெண்டரில் சேர்க்க',
        getDirections: 'வழித்தடம் பார்க்க (GPS)',
        willYouAttend: 'விழாவில் கலந்து கொள்வீர்களா?',
        acceptJoy: 'மகிழ்ச்சியுடன் வருகிறோம் 🎉',
        declineRegret: 'வர இயலாமைக்கு வருந்துகிறோம் 💌',
        maybeTentative: 'இன்னும் முடிவாகவில்லை 🤔',
        totalAttendees: 'வருகை தரும் மொத்த நபர்கள்',
        adults: 'பெரியவர்கள் (12+ வயது)',
        kids: 'குழந்தைகள் (12 வயதுக்கு கீழ்)',
        dietaryHeading: '🍲 உணவு விருப்பம்',
        dietaryDesc: 'உங்களுக்கும் உங்கள் குடும்பத்திற்கும் சுவையான உணவு ஏற்பாடு செய்ய உதவவும்.',
        dietNone: 'சாதாரண உணவு',
        dietVeg: 'சைவம் 🥦',
        dietNonVeg: 'அசைவம் 🍗',
        dietJain: 'ஜெயின் உணவு 🌿',
        dietVegan: 'வீகன் 🌱',
        dietHalal: 'ஹலால் 🥩',
        dietEggetarian: 'முட்டை உணவு 🍳',
        accommodationHeading: '🏨 தங்குமிடம் & பயண வசதி',
        accommodationDesc: 'தங்குமிடம் அல்லது பயண உதவி தேவைப்பட்டால் குறிப்பிடவும்.',
        accommodationYes: 'ஆம், தங்குமிடம் ஏற்பாடு செய்யவும் 🏨',
        accommodationNo: 'இல்லை, சொந்தமாக ஏற்பாடு செய்துள்ளோம் 🚗',
        arrivalDate: 'வருகை தரும் தேதி மற்றும் நேரம்',
        travelDetails: 'விமானம் / ரயில் / வாகன விவரம்',
        wishesHeading: '💌 வாழ்த்துகள் மற்றும் ஆசிகள்',
        wishesPlaceholder: 'மணமக்கள் மற்றும் குடும்பத்தினருக்கு உங்கள் வாழ்த்துகளை பகிரவும்...',
        submitRsvp: 'RSVP உறுதிப்படுத்துக 💌',
        updateRsvp: 'RSVP புதுப்பிக்க ✏️',
        submitting: 'பதிவு செய்யப்படுகிறது...',
        thankYouAccepted: '🎉 மிக்க நன்றி! உங்கள் வரவை ஆவலுடன் எதிர்நோக்குகிறோம்!',
        thankYouDeclined: '💌 உங்கள் வருகை இல்லாமை வருத்தமளிக்கிறது, தெரிவித்ததற்கு நன்றி.',
        thankYouMaybe: '⏳ நன்றி! உறுதி செய்தபின் தயவுசெய்து தெரிவிக்கவும்.',
        thankYouSubtitle: 'உங்கள் பதில் குடும்பத்தினருக்கு அனுப்பப்பட்டது.',
        downloadInviteCard: '📥 அழைப்பிதழ் பதிவிறக்கம் செய்ய',
        editRsvpBtn: '✏️ பதிலை மாற்றுக',
        switchLanguage: 'மொழி',
    },
    te: {
        hostedBy: 'ఆహ్వానించేవారు:',
        days: 'రోజులు',
        hours: 'గంటలు',
        mins: 'నిమిషాలు',
        eventLive: 'ఈరోజు శుభ సమయం! 🎉',
        saveTheDate: 'శుభ వివాహ మహోత్సవం',
        when: 'తేదీ మరియు సమయం',
        where: 'వేదిక మరియు చిరునామా',
        addToCalendar: 'క్యాలెండర్‌లో చేర్చండి',
        getDirections: 'మార్గం చూడండి (GPS)',
        willYouAttend: 'మీరు వేడుకకు విచ్చేస్తున్నారా?',
        acceptJoy: 'సంతోషంగా హాజరవుతాము 🎉',
        declineRegret: 'రాలేకపోతున్నందుకు చింతిస్తున్నాము 💌',
        maybeTentative: 'ఇంకా ఖరారు కాలేదు 🤔',
        totalAttendees: 'హాజరయ్యే మొత్తం అతిధుల సంఖ్య',
        adults: 'పెద్దలు (12+ సం)',
        kids: 'పిల్లలు (12 సం లోపు)',
        dietaryHeading: '🍲 భోజన ప్రాధాన్యతలు',
        dietaryDesc: 'మీకు మరియు మీ కుటుంబానికి రుచికరమైన ఆహార ఏర్పాట్ల కోసం ఎంచుకోండి.',
        dietNone: 'సాధారణ భోజనం',
        dietVeg: 'శాకాహారం 🥦',
        dietNonVeg: 'మాంసాహారం 🍗',
        dietJain: 'జైన భోజనం 🌿',
        dietVegan: 'వీగన్ 🌱',
        dietHalal: 'హలాల్ 🥩',
        dietEggetarian: 'ఎగ్గేటేరియన్ 🍳',
        accommodationHeading: '🏨 వసతి & ప్రయాణ సహాయం',
        accommodationDesc: 'మీకు బస లేదా ప్రయాణ ఏర్పాట్లు అవసరమైతే తెలపండి.',
        accommodationYes: 'అవును, దయచేసి వసతి ఏర్పాటు చేయండి 🏨',
        accommodationNo: 'వద్దు, స్వయంగా చూసుకుంటాము 🚗',
        arrivalDate: 'చేరుకునే తేదీ & సమయం',
        travelDetails: 'రైలు / విమానం / వాహన వివరాలు',
        wishesHeading: '💌 శుభాకాంక్షలు & ఆశీస్సులు',
        wishesPlaceholder: 'నూతన జంట మరియు కుటుంబం కోసం మీ శుభాకాంక్షలు రాయండి...',
        submitRsvp: 'RSVP ధృవీకరించండి 💌',
        updateRsvp: 'RSVP అప్‌డేట్ చేయండి ✏️',
        submitting: 'సమర్పిస్తోంది...',
        thankYouAccepted: '🎉 ధన్యవాదాలు! మీ రాక కోసం ఎదురుచూస్తున్నాము!',
        thankYouDeclined: '💌 మీ లేని లోటు తెలుస్తుంది, తెలియజేసినందుకు ధన్యవాదాలు.',
        thankYouMaybe: '⏳ ధన్యవాదాలు! ప్లాన్ ఖరారయ్యాక దయచేసి తెలియజేయండి.',
        thankYouSubtitle: 'మీ సమాధానం విజయవంతంగా కుటుంబానికి చేరింది.',
        downloadInviteCard: '📥 ఆహ్వాన పత్రిక డౌన్‌లోడ్ చేసుకోండి',
        editRsvpBtn: '✏️ సమాధానం మార్చండి',
        switchLanguage: 'భాష',
    },
    hinglish: {
        hostedBy: 'Hosted by',
        days: 'Din',
        hours: 'Ghante',
        mins: 'Mins',
        eventLive: 'Aaj Celebration ka Din Hai! 🎉',
        saveTheDate: 'Save The Date',
        when: 'Date & Time',
        where: 'Venue & Address',
        addToCalendar: 'Calendar mein add karein',
        getDirections: 'Directions dekhein (GPS)',
        willYouAttend: 'Kya aap celebration mein aa rahe hain?',
        acceptJoy: 'Bilkul Aayenge! 🎉',
        declineRegret: 'Sorry, nahi aa payenge 💌',
        maybeTentative: 'Abhi pakka nahi 🤔',
        totalAttendees: 'Aane wale guests ki total count',
        adults: 'Adults (12+ yrs)',
        kids: 'Kids (under 12 yrs)',
        dietaryHeading: '🍲 Food Preferences',
        dietaryDesc: 'Aapke aur family ke liye best food arrangements ke liye select karein.',
        dietNone: 'Standard Food',
        dietVeg: 'Pure Veg 🥦',
        dietNonVeg: 'Non-Veg 🍗',
        dietJain: 'Jain Food 🌿',
        dietVegan: 'Vegan 🌱',
        dietHalal: 'Halal 🥩',
        dietEggetarian: 'Eggetarian 🍳',
        accommodationHeading: '🏨 Stay & Travel Assistance',
        accommodationDesc: 'Agar hotel stay ya travel assistance chahiye toh please batayein.',
        accommodationYes: 'Haan, please hotel stay arrange karein 🏨',
        accommodationNo: 'Nahi, hum khud manage kar lenge 🚗',
        arrivalDate: 'Expected Arrival Date & Time',
        travelDetails: 'Flight / Train / Car Details',
        wishesHeading: '💌 Best Wishes & Blessings',
        wishesPlaceholder: 'Host family aur couple ke liye apna pyaara message yahan likhein...',
        submitRsvp: 'Submit RSVP Confirmation 💌',
        updateRsvp: 'Update My RSVP ✏️',
        submitting: 'Submitting response...',
        thankYouAccepted: '🎉 Shandar! Hum aapka besabri se intezar karenge!',
        thankYouDeclined: '💌 Aapki kami mehsoos hogi, batane ke liye shukriya.',
        thankYouMaybe: '⏳ Shukriya! Jaise hi plan confirm ho, please update karein.',
        thankYouSubtitle: 'Aapka response host family tak successfully pahunch gaya hai.',
        downloadInviteCard: '📥 Download E-Invite Card',
        editRsvpBtn: '✏️ Response Edit Karein',
        switchLanguage: 'Language',
    },
    es: {
        hostedBy: 'Anfitrión:',
        days: 'Días',
        hours: 'Horas',
        mins: 'Min',
        eventLive: '¡Hoy es el Gran Día! 🎉',
        saveTheDate: 'Reserva la Fecha',
        when: 'Fecha y Hora',
        where: 'Lugar y Dirección',
        addToCalendar: 'Añadir al Calendario',
        getDirections: 'Cómo Llegar (GPS)',
        willYouAttend: '¿Nos acompañarás en la celebración?',
        acceptJoy: '¡Acepto con Alegría! 🎉',
        declineRegret: 'Lamentablemente no podré 💌',
        maybeTentative: 'Por Confirmar 🤔',
        totalAttendees: 'Número Total de Asistentes',
        adults: 'Adultos (12+ años)',
        kids: 'Niños (menores de 12 años)',
        dietaryHeading: '🍲 Preferencias Dietéticas',
        dietaryDesc: 'Ayúdanos a coordinar un menú perfecto para ti y tu familia.',
        dietNone: 'Estándar / Sin preferencia',
        dietVeg: 'Vegetariano 🥦',
        dietNonVeg: 'No Vegetariano 🍗',
        dietJain: 'Comida Jain 🌿',
        dietVegan: 'Vegano 🌱',
        dietHalal: 'Halal 🥩',
        dietEggetarian: 'Ovolactovegetariano 🍳',
        accommodationHeading: '🏨 Alojamiento y Transporte',
        accommodationDesc: 'Indícanos si requieres asistencia con hotel o traslados.',
        accommodationYes: 'Sí, requiero hospedaje / hotel 🏨',
        accommodationNo: 'No, gestionaré mi estancia 🚗',
        arrivalDate: 'Fecha y Hora Estimada de Llegada',
        travelDetails: 'Detalles de Vuelo / Tren / Vehículo',
        wishesHeading: '💌 Mensaje y Buenos Deseos',
        wishesPlaceholder: 'Escribe un mensaje de felicitación para los anfitriones...',
        submitRsvp: 'Confirmar Asistencia (RSVP) 💌',
        updateRsvp: 'Actualizar Mi Respuesta ✏️',
        submitting: 'Guardando respuesta...',
        thankYouAccepted: '🎉 ¡Maravilloso! ¡Será un placer celebrar juntos!',
        thankYouDeclined: '💌 Sentiremos tu ausencia, gracias por avisarnos.',
        thankYouMaybe: '⏳ ¡Gracias! Por favor confirma tus planes cuando puedas.',
        thankYouSubtitle: 'Tu respuesta ha sido enviada exitosamente a los anfitriones.',
        downloadInviteCard: '📥 Descargar Tarjeta de Invitación',
        editRsvpBtn: '✏️ Modificar Respuesta',
        switchLanguage: 'Idioma',
    },
    fr: {
        hostedBy: 'Organisé par :',
        days: 'Jours',
        hours: 'Heures',
        mins: 'Min',
        eventLive: "C'est le grand jour ! 🎉",
        saveTheDate: 'Réservez la Date',
        when: 'Date et Heure',
        where: 'Lieu et Adresse',
        addToCalendar: 'Ajouter au Calendrier',
        getDirections: "Itinéraire d'accès (GPS)",
        willYouAttend: 'Serez-vous parmi nous ?',
        acceptJoy: 'Accepte avec Joie 🎉',
        declineRegret: 'Ne peut pas assister 💌',
        maybeTentative: 'Peut-être / En attente 🤔',
        totalAttendees: 'Nombre total de participants',
        adults: 'Adultes (12+ ans)',
        kids: 'Enfants (moins de 12 ans)',
        dietaryHeading: '🍲 Préférences Alimentaires',
        dietaryDesc: 'Aidez-nous à préparer un repas sur mesure pour vous et votre famille.',
        dietNone: 'Standard / Aucune',
        dietVeg: 'Végétarien 🥦',
        dietNonVeg: 'Non Végétarien 🍗',
        dietJain: 'Régime Jaïn 🌿',
        dietVegan: 'Végan 🌱',
        dietHalal: 'Halal 🥩',
        dietEggetarian: 'Végétarien avec œufs 🍳',
        accommodationHeading: '🏨 Hébergement et Transport',
        accommodationDesc: 'Faites-nous savoir si vous avez besoin d’aide pour un hôtel ou transport.',
        accommodationYes: 'Oui, veuillez réserver un hébergement 🏨',
        accommodationNo: 'Non, je gère mon propre séjour 🚗',
        arrivalDate: "Date et heure d'arrivée prévues",
        travelDetails: 'Détails de vol / train / voiture',
        wishesHeading: '💌 Vœux et Félicitations',
        wishesPlaceholder: 'Laissez un message chaleureux aux hôtes...',
        submitRsvp: 'Confirmer ma Présence (RSVP) 💌',
        updateRsvp: 'Mettre à jour ma réponse ✏️',
        submitting: 'Envoi en cours...',
        thankYouAccepted: '🎉 Magnifique ! Nous avons hâte de célébrer avec vous !',
        thankYouDeclined: '💌 Vous nous manquerez, merci de nous avoir prévenus.',
        thankYouMaybe: '⏳ Merci ! Confirmez dès que possible.',
        thankYouSubtitle: 'Votre réponse a été transmise aux organisateurs.',
        downloadInviteCard: "📥 Télécharger la Carte d'Invitation",
        editRsvpBtn: '✏️ Modifier ma réponse',
        switchLanguage: 'Langue',
    },
    de: {
        hostedBy: 'Gastgeber:',
        days: 'Tage',
        hours: 'Std.',
        mins: 'Min.',
        eventLive: 'Heute ist der Festtag! 🎉',
        saveTheDate: 'Save the Date',
        when: 'Datum & Uhrzeit',
        where: 'Veranstaltungsort & Adresse',
        addToCalendar: 'Zum Kalender hinzufügen',
        getDirections: 'Route berechnen (GPS)',
        willYouAttend: 'Nimmst du an der Feier teil?',
        acceptJoy: 'Zusage mit Freude 🎉',
        declineRegret: 'Leider absagen 💌',
        maybeTentative: 'Noch unentschlossen 🤔',
        totalAttendees: 'Gesamtzahl der Gäste',
        adults: 'Erwachsene (12+ Jahre)',
        kids: 'Kinder (unter 12 Jahren)',
        dietaryHeading: '🍲 Essenspräferenzen',
        dietaryDesc: 'Hilf uns, die beste Verpflegung für dich und deine Familie zu planen.',
        dietNone: 'Standard / Keine Einschränkung',
        dietVeg: 'Vegetarisch 🥦',
        dietNonVeg: 'Nicht-vegetarisch 🍗',
        dietJain: 'Jain-Kost 🌿',
        dietVegan: 'Vegan 🌱',
        dietHalal: 'Halal 🥩',
        dietEggetarian: 'Ovo-vegetarisch 🍳',
        accommodationHeading: '🏨 Unterkunft & Anreise',
        accommodationDesc: 'Gib uns Bescheid, falls du Unterstützung bei der Übernachtung benötigst.',
        accommodationYes: 'Ja, bitte Unterkunft organisieren 🏨',
        accommodationNo: 'Nein, organisiere Aufenthalt selbst 🚗',
        arrivalDate: 'Voraussichtliche Ankunft',
        travelDetails: 'Flug- / Zug- / Fahrzeuginformationen',
        wishesHeading: '💌 Glückwünsche & Nachricht',
        wishesPlaceholder: 'Hinterlasse eine persönliche Nachricht an die Gastgeber...',
        submitRsvp: 'RSVP-Antwort absenden 💌',
        updateRsvp: 'Antwort aktualisieren ✏️',
        submitting: 'Antwort wird übermittelt...',
        thankYouAccepted: '🎉 Wunderbar! Wir freuen uns sehr auf dein Kommen!',
        thankYouDeclined: '💌 Schade, dass du nicht dabei sein kannst. Danke für die Rückmeldung.',
        thankYouMaybe: '⏳ Vielen Dank! Bitte gib Bescheid, sobald deine Pläne feststehen.',
        thankYouSubtitle: 'Deine Antwort wurde erfolgreich an die Gastgeber übermittelt.',
        downloadInviteCard: '📥 Einladungskarte herunterladen',
        editRsvpBtn: '✏️ Antwort bearbeiten',
        switchLanguage: 'Sprache',
    },
    it: {
        hostedBy: 'Ospitato da:',
        days: 'Giorni',
        hours: 'Ore',
        mins: 'Min',
        eventLive: 'Oggi è il grande giorno! 🎉',
        saveTheDate: 'Save The Date',
        when: 'Data e Ora',
        where: 'Luogo e Indirizzo',
        addToCalendar: 'Aggiungi al Calendario',
        getDirections: 'Indicazioni Stradali (GPS)',
        willYouAttend: 'Parteciperai alla celebrazione?',
        acceptJoy: 'Accetto con Gioia 🎉',
        declineRegret: 'Purtroppo non posso 💌',
        maybeTentative: 'In Forse 🤔',
        totalAttendees: 'Totale Partecipanti',
        adults: 'Adulti (12+ anni)',
        kids: 'Bambini (sotto i 12 anni)',
        dietaryHeading: '🍲 Preferenze Alimentari',
        dietaryDesc: 'Aiutaci a organizzare un menù speciale per te e la tua famiglia.',
        dietNone: 'Standard / Nessuna',
        dietVeg: 'Vegetariano 🥦',
        dietNonVeg: 'Non Vegetariano 🍗',
        dietJain: 'Cucina Jain 🌿',
        dietVegan: 'Vegano 🌱',
        dietHalal: 'Halal 🥩',
        dietEggetarian: 'Ovo-vegetariano 🍳',
        accommodationHeading: '🏨 Alloggio e Viaggio',
        accommodationDesc: 'Facci sapere se hai bisogno di assistenza per hotel o transfer.',
        accommodationYes: 'Sì, ho bisogno di un alloggio 🏨',
        accommodationNo: 'No, provvederò autonomamente 🚗',
        arrivalDate: 'Data e Ora di Arrivo Prevista',
        travelDetails: 'Dettagli Volo / Treno / Auto',
        wishesHeading: '💌 Auguri e Messaggio',
        wishesPlaceholder: 'Scrivi un messaggio di affetto per gli sposi e la famiglia...',
        submitRsvp: 'Invia Conferma (RSVP) 💌',
        updateRsvp: 'Aggiorna Risposta ✏️',
        submitting: 'Invio risposta in corso...',
        thankYouAccepted: '🎉 Fantastico! Non vediamo l’ora di festeggiare insieme!',
        thankYouDeclined: '💌 Ci mancherai, grazie per avercelo comunicato.',
        thankYouMaybe: '⏳ Grazie! Facci sapere non appena i tuoi piani saranno definiti.',
        thankYouSubtitle: 'La tua risposta è stata inviata con successo agli organizzatori.',
        downloadInviteCard: '📥 Scarica Biglietto di Invito',
        editRsvpBtn: '✏️ Modifica Risposta',
        switchLanguage: 'Lingua',
    },
    ar: {
        hostedBy: 'المضيف:',
        days: 'أيام',
        hours: 'ساعات',
        mins: 'دقائق',
        eventLive: 'اليوم يوم الاحتفال! 🎉',
        saveTheDate: 'احفظ التاريخ',
        when: 'التاريخ والوقت',
        where: 'المكان والعنوان',
        addToCalendar: 'إضافة إلى التقويم',
        getDirections: 'الاتجاهات (GPS)',
        willYouAttend: 'هل ستشرفنا بحضورك؟',
        acceptJoy: 'يشرفني الحضور بكل سرور 🎉',
        declineRegret: 'أعتذر عن عدم الحضور 💌',
        maybeTentative: 'ربما / قيد الترتيب 🤔',
        totalAttendees: 'إجمالي عدد الحضور',
        adults: 'البالغون (12+ سنة)',
        kids: 'الأطفال (أقل من 12 سنة)',
        dietaryHeading: '🍲 التفضيلات الغذائية',
        dietaryDesc: 'ساعدنا في ترتيب أشهى المأكولات المناسبة لك ولعائلتك.',
        dietNone: 'طعام عادي / بدون تفضيل خاص',
        dietVeg: 'نباتي 🥦',
        dietNonVeg: 'غير نباتي 🍗',
        dietJain: 'طعام جين 🌿',
        dietVegan: 'نباتي صرف (فيغان) 🌱',
        dietHalal: 'حلال 🥩',
        dietEggetarian: 'نباتي مع بيض 🍳',
        accommodationHeading: '🏨 الإقامة وتسهيلات السفر',
        accommodationDesc: 'يرجى إعلامنا إذا كنت بحاجة إلى حجز فندقي أو وسيلة مواصلات.',
        accommodationYes: 'نعم، يرجى ترتيب الإقامة 🏨',
        accommodationNo: 'لا، سأرتب إقامتي بنفسي 🚗',
        arrivalDate: 'تاريخ ووقت الوصول المتوقع',
        travelDetails: 'تفاصيل الرحلة / الطيران / القطار',
        wishesHeading: '💌 التهاني والتبريكات',
        wishesPlaceholder: 'اكتب رسالة تهنئة ومباركة للمضيف والعائلة...',
        submitRsvp: 'تأكيد الحضور (RSVP) 💌',
        updateRsvp: 'تحديث الرد ✏️',
        submitting: 'جاري تسجيل الرد...',
        thankYouAccepted: '🎉 رائع جداً! نتطلع بشوق للاحتفال معك!',
        thankYouDeclined: '💌 سنفتقد وجودك، وشكراً لإعلامنا مسبقاً.',
        thankYouMaybe: '⏳ شكراً لك! يرجى تأكيد قرارك النهائي حينما يتيسر لك.',
        thankYouSubtitle: 'تم إرسال ردك بنجاح إلى المضيف.',
        downloadInviteCard: '📥 تحميل بطاقة الدعوة الإلكترونية',
        editRsvpBtn: '✏️ تعديل الرد',
        switchLanguage: 'اللغة',
    },
    pt: {
        hostedBy: 'Anfitrião:',
        days: 'Dias',
        hours: 'Horas',
        mins: 'Min',
        eventLive: 'Hoje é o Grande Dia! 🎉',
        saveTheDate: 'Reserve a Data',
        when: 'Data e Horário',
        where: 'Local e Endereço',
        addToCalendar: 'Adicionar ao Calendário',
        getDirections: 'Como Chegar (GPS)',
        willYouAttend: 'Você estará presente?',
        acceptJoy: 'Confirmo com Alegria! 🎉',
        declineRegret: 'Infelizmente não poderei 💌',
        maybeTentative: 'Talvez / A Confirmar 🤔',
        totalAttendees: 'Total de Convidados',
        adults: 'Adultos (12+ anos)',
        kids: 'Crianças (menos de 12 anos)',
        dietaryHeading: '🍲 Preferências Alimentares',
        dietaryDesc: 'Ajude-nos a organizar um cardápio delicioso para você e sua família.',
        dietNone: 'Padrão / Sem Restrições',
        dietVeg: 'Vegetariano 🥦',
        dietNonVeg: 'Não Vegetariano 🍗',
        dietJain: 'Comida Jain 🌿',
        dietVegan: 'Vegano 🌱',
        dietHalal: 'Halal 🥩',
        dietEggetarian: 'Ovolactovegetariano 🍳',
        accommodationHeading: '🏨 Hospedagem e Transporte',
        accommodationDesc: 'Informe se precisa de ajuda com hotel ou transporte local.',
        accommodationYes: 'Sim, favor providenciar hospedagem 🏨',
        accommodationNo: 'Não, cuidarei da minha estadia 🚗',
        arrivalDate: 'Data e Hora Prevista de Chegada',
        travelDetails: 'Detalhes do Voo / Trem / Veículo',
        wishesHeading: '💌 Mensagem e Votos de Felicidades',
        wishesPlaceholder: 'Escreva uma mensagem carinhosa para a família e anfitriões...',
        submitRsvp: 'Confirmar Presença (RSVP) 💌',
        updateRsvp: 'Atualizar Resposta ✏️',
        submitting: 'Enviando resposta...',
        thankYouAccepted: '🎉 Maravilhoso! Estamos ansiosos para celebrar juntos!',
        thankYouDeclined: '💌 Sentiremos sua falta, obrigado por nos avisar.',
        thankYouMaybe: '⏳ Obrigado! Confirme seus planos assim que possível.',
        thankYouSubtitle: 'Sua resposta foi enviada com sucesso aos anfitriões.',
        downloadInviteCard: '📥 Baixar Cartão de Convite',
        editRsvpBtn: '✏️ Editar Resposta',
        switchLanguage: 'Idioma',
    },
    ru: {
        hostedBy: 'Организатор:',
        days: 'Дней',
        hours: 'Часов',
        mins: 'Мин',
        eventLive: 'Сегодня день праздника! 🎉',
        saveTheDate: 'Сохраните Дату',
        when: 'Дата и время',
        where: 'Место и адрес',
        addToCalendar: 'Добавить в календарь',
        getDirections: 'Маршрут на карте (GPS)',
        willYouAttend: 'Сможете ли вы присутствовать?',
        acceptJoy: 'С радостью приду! 🎉',
        declineRegret: 'К сожалению, не смогу 💌',
        maybeTentative: 'Пока не уверен 🤔',
        totalAttendees: 'Всего гостей',
        adults: 'Взрослые (12+ лет)',
        kids: 'Дети (до 12 лет)',
        dietaryHeading: '🍲 Предпочтения в еде',
        dietaryDesc: 'Помогите нам организовать вкусные блюда для вас и вашей семьи.',
        dietNone: 'Стандартное меню',
        dietVeg: 'Вегетарианское 🥦',
        dietNonVeg: 'Мясное / Рыбное 🍗',
        dietJain: 'Джайнское меню 🌿',
        dietVegan: 'Веганское 🌱',
        dietHalal: 'Халяль 🥩',
        dietEggetarian: 'С яйцами 🍳',
        accommodationHeading: '🏨 Проживание и транспорт',
        accommodationDesc: 'Сообщите, если вам требуется помощь с отелем или трансфером.',
        accommodationYes: 'Да, требуется проживание в отеле 🏨',
        accommodationNo: 'Нет, организую проживание самостоятельно 🚗',
        arrivalDate: 'Ориентировочная дата и время прибытия',
        travelDetails: 'Номер рейса / поезда / автомобиля',
        wishesHeading: '💌 Поздравления и пожелания',
        wishesPlaceholder: 'Напишите ваши тёплые пожелания для семьи и молодожёнов...',
        submitRsvp: 'Подтвердить присутствие (RSVP) 💌',
        updateRsvp: 'Обновить ответ ✏️',
        submitting: 'Отправка ответа...',
        thankYouAccepted: '🎉 Прекрасно! С нетерпением ждём встречи с вами!',
        thankYouDeclined: '💌 Нам будет вас не хватать, спасибо, что сообщили.',
        thankYouMaybe: '⏳ Спасибо! Пожалуйста, подтвердите планы, когда определитесь.',
        thankYouSubtitle: 'Ваш ответ успешно передан организаторам.',
        downloadInviteCard: '📥 Скачать пригласительную открытку',
        editRsvpBtn: '✏️ Изменить ответ',
        switchLanguage: 'Язык',
    },
    ja: {
        hostedBy: '主催者:',
        days: '日',
        hours: '時間',
        mins: '分',
        eventLive: '本日、祝宴の日です！ 🎉',
        saveTheDate: 'ご案内',
        when: '日時',
        where: '会場・住所',
        addToCalendar: 'カレンダーに追加',
        getDirections: 'アクセスマップ (GPS)',
        willYouAttend: 'ご出席いただけますでしょうか？',
        acceptJoy: '喜んで出席します 🎉',
        declineRegret: '残念ながら欠席します 💌',
        maybeTentative: '未定 / 検討中 🤔',
        totalAttendees: 'ご出席人数',
        adults: '大人 (12歳以上)',
        kids: 'お子様 (12歳未満)',
        dietaryHeading: '🍲 お食事のお好み・アレルギー',
        dietaryDesc: '皆様に安心してお召し上がりいただけるようご希望をお知らせください。',
        dietNone: '指定なし（通常メニュー）',
        dietVeg: 'ベジタリアン 🥦',
        dietNonVeg: 'ノンベジタリアン 🍗',
        dietJain: 'ジャイナ教対応 🌿',
        dietVegan: 'ヴィーガン 🌱',
        dietHalal: 'ハラール 🥩',
        dietEggetarian: '卵料理可 🍳',
        accommodationHeading: '🏨 ご宿泊・交通手配',
        accommodationDesc: 'ホテル宿泊や交通機関のご手配が必要な場合はお知らせください。',
        accommodationYes: 'はい、宿泊の手配を希望します 🏨',
        accommodationNo: 'いいえ、自身で手配します 🚗',
        arrivalDate: '到着予定日時',
        travelDetails: '便名・列車・移動手段の詳細',
        wishesHeading: '💌 お祝いメッセージ',
        wishesPlaceholder: '主催者やご家族へのメッセージをご記入ください...',
        submitRsvp: '出欠を送信する (RSVP) 💌',
        updateRsvp: '回答を更新する ✏️',
        submitting: '送信中...',
        thankYouAccepted: '🎉 ありがとうございます！皆様とお会いできることを心より楽しみにしています！',
        thankYouDeclined: '💌 ご都合がつかず残念ですが、ご連絡いただき誠にありがとうございました。',
        thankYouMaybe: '⏳ ありがとうございます。ご予定が決まり次第お知らせください。',
        thankYouSubtitle: 'ご回答は主催者に無事送信されました。',
        downloadInviteCard: '📥 招待カードをダウンロード',
        editRsvpBtn: '✏️ 回答を変更する',
        switchLanguage: '言語',
    },
    zh: {
        hostedBy: '诚邀人：',
        days: '天',
        hours: '小时',
        mins: '分钟',
        eventLive: '今日大吉，喜庆华堂！ 🎉',
        saveTheDate: '谨定日程',
        when: '吉日良辰',
        where: '喜筵地点',
        addToCalendar: '添加至日历',
        getDirections: '查看地图导航 (GPS)',
        willYouAttend: '您是否出席本次盛会？',
        acceptJoy: '欣然应邀 🎉',
        declineRegret: '遗憾未能出席 💌',
        maybeTentative: '暂未确定 🤔',
        totalAttendees: '出席总人数',
        adults: '成人 (12岁以上)',
        kids: '儿童 (12岁以下)',
        dietaryHeading: '🍲 膳食喜好与禁忌',
        dietaryDesc: '请告知您的用餐偏好，以便我们为您和家人准备贴心佳肴。',
        dietNone: '标准宴席 / 无特殊要求',
        dietVeg: '素食 🥦',
        dietNonVeg: '荤食 🍗',
        dietJain: '耆那教素食 🌿',
        dietVegan: '纯素 🌱',
        dietHalal: '清真 🥩',
        dietEggetarian: '蛋素 🍳',
        accommodationHeading: '🏨 住宿与出行协助',
        accommodationDesc: '如需协助安排酒店住宿或接送，请告知我们。',
        accommodationYes: '需要，请协助安排住宿 🏨',
        accommodationNo: '不需要，自行安排 / 本地居住 🚗',
        arrivalDate: '预计到达日期与时间',
        travelDetails: '航班 / 车次 / 自驾信息',
        wishesHeading: '💌 祝福寄语',
        wishesPlaceholder: '为新人及东道家庭写下您的温馨祝福...',
        submitRsvp: '确认并提交回执 (RSVP) 💌',
        updateRsvp: '更新回执 ✏️',
        submitting: '正在提交...',
        thankYouAccepted: '🎉 太棒了！衷心期待与您共襄盛举！',
        thankYouDeclined: '💌 虽遗憾未能相聚，由衷感谢您的告知与祝福。',
        thankYouMaybe: '⏳ 收到！行程确定后请随时更新告知。',
        thankYouSubtitle: '您的回执已成功发送至主办方。',
        downloadInviteCard: '📥 下载电子请柬',
        editRsvpBtn: '✏️ 修改回执',
        switchLanguage: '语言',
    },
};

export const getRsvpTranslation = (langCode: string): RsvpTranslations => {
    return RSVP_TRANSLATIONS[langCode] || RSVP_TRANSLATIONS['en'];
};
